import { useQueryClient } from '@tanstack/react-query';
import { useUpdateLead } from 'hooks/useUpdateLead';
import { useLeadsStore } from 'store/leadsStore';

interface Props {
    status?: string;
    className?: string;
    leadId?: string;
    isOpen: boolean;
    onToggle: () => void;
}

const CustomBadge = ({ status = 'no-status', className, leadId, isOpen, onToggle }: Props) => {
    const { mutate } = useUpdateLead();
    const queryClient = useQueryClient();
    const { currentPage, status: currentStatus } = useLeadsStore();

    const statusOptions = ['contacted', 'follow-up', 'active', 'inactive', 'qualified', 'closed'];

    const getStatusStyles = (status: string) => {
        const baseStyles = 'px-4 py-1.5 rounded-lg text-lg text-center font-medium cursor-pointer';

        const statusStyles: Record<string, string> = {
            contacted: 'bg-cyan-100 text-cyan-600',
            'follow-up': 'bg-purple-100 text-purple-600',
            active: 'bg-green-100 text-green-600',
            inactive: 'bg-orange-100 text-orange-600',
            qualified: 'bg-indigo-100 text-indigo-600',
            closed: 'bg-red-100 text-red-600',
        };

        return `${baseStyles} ${statusStyles[status.toLowerCase()] || statusStyles['no-status']}`;
    };

    const formatStatus = (status: string) => {
        if (status === 'no-status') return 'No Status';
        return status
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const handleStatusChange = (newStatus: string) => {
        mutate({ id: leadId!, status: newStatus });
        const cachedData = queryClient.getQueryData(['leads', currentPage, newStatus]);

        if (cachedData) {
            queryClient.resetQueries({ queryKey: ['leads', currentPage, status] });
        }
        if (currentStatus) {
            queryClient.resetQueries({ queryKey: ['leads', currentPage, ''] });
        }
        onToggle();
    };

    return (
        <div className="relative w-fit">
            <div className={`${getStatusStyles(status)} ${className}`} onClick={onToggle}>
                {formatStatus(status)}
            </div>

            {isOpen && (
                <div className="absolute z-10 mt-2 w-fit bg-white border border-gray-200 rounded-lg shadow-lg cursor-pointer">
                    {statusOptions.map((option) => (
                        <div
                            key={option}
                            className={`${getStatusStyles(option)} border-b px-4 py-2 last:border-b-0 m-2`}
                            onClick={() => handleStatusChange(option)}
                        >
                            {formatStatus(option)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CustomBadge;
